import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { Spinner } from '../ui/spinner';
import { Table, TableHeader, TableBody, TableRow, TableCell } from '../ui/table';
import { ToggleGroup, ToggleItem } from '../ui/toggle';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { gameDepartmentAPI } from '../../services/api';
import { SortableHead, TablePagination } from './TableControls';
import useBrandChange from './useBrandChange';
import { ALL, featureGroupOptions, filterParams, producerOptions } from './metricsFilters';

const METRIC_COLOR = '#3861FB';
const AXIS_COLOR = '#7a8499';

const METRICS = [
  { key: 'turnover_eur', label: 'Turnover', fmt: fmtEur },
  { key: 'ggr_eur',      label: 'GGR',      fmt: fmtEur },
  { key: 'actives',      label: 'Actives',  fmt: fmtInt },
  { key: 'n_bets',       label: 'Bets',     fmt: fmtInt },
  { key: 'payout_pct',   label: 'Payout',   fmt: fmtPct },
];
const STRING_COLS = new Set(['game_title', 'provider', 'producer', 'feature_group']);
const TOP_N = 12;
const TABLE_PAGE_SIZE = 50;

const VIEWS = [
  { key: 'game',     label: 'By Game' },
  { key: 'provider', label: 'By Provider' },
  { key: 'producer', label: 'By Producer' },
];

// By Producer can optionally split each producer into its feature groups
// (feature groups only mean something per producer).
const PRODUCER_GROUPINGS = [
  { key: 'producer',      label: 'Producer' },
  { key: 'feature_group', label: 'Producer – feature group' },
];

const VIEW_TITLES = {
  game: 'Game Metrics', provider: 'Provider Metrics', producer: 'Producer Metrics', feature_group: 'Producer – Feature Group Metrics',
};

// Per view: the API call, the response field holding its rows and the
// singular noun.
const VIEW_SOURCES = {
  game:     { fetch: req => gameDepartmentAPI.getGameMetrics(req),     field: 'games',     noun: 'game' },
  provider: { fetch: req => gameDepartmentAPI.getProviderMetrics(req), field: 'providers', noun: 'provider' },
  producer: { fetch: req => gameDepartmentAPI.getProducerMetrics(req), field: 'producers', noun: 'producer' },
  // Feature groups are per producer, so a row is a producer + group pair.
  feature_group: { fetch: req => gameDepartmentAPI.getFeatureGroupMetrics(req), field: 'feature_groups', noun: 'producer – feature group' },
};

function rowKey(row, viewMode) {
  return viewMode === 'feature_group' ? `${row.producer}|${row.feature_group}` : row[viewMode];
}

const DATE_RANGE_PRESETS = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
];

function fmtInt(v) { return Number(v).toLocaleString('en-GB'); }
function fmtEur(v) { return `€${Number(v).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`; }
function fmtPct(v) { return v == null ? '—' : `${Number(v).toFixed(2)}%`; }

// Numeric comparator that always sinks nulls (e.g. payout_pct on zero
// turnover) to the end, regardless of sort direction.
function compareNumeric(a, b, dir) {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return dir === 'asc' ? a - b : b - a;
}
function truncate(s, n) { return s && s.length > n ? `${s.slice(0, n - 1)}…` : s; }

function todayUtc() {
  return new Date(new Date().toISOString().slice(0, 10));
}

function rangeForLastDays(days) {
  const to = todayUtc();
  const from = new Date(to);
  from.setUTCDate(from.getUTCDate() - days);
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

// [from, to) against group_created_at — `to` here is a calendar date, so the
// request's upper bound is midnight the day AFTER it, to include that whole day.
function toRequestRange(range) {
  const to = new Date(`${range.to}T00:00:00.000Z`);
  to.setUTCDate(to.getUTCDate() + 1);
  return { from: `${range.from}T00:00:00.000Z`, to: to.toISOString() };
}

function rowLabel(row, viewMode) {
  if (viewMode === 'provider') return row.provider;
  if (viewMode === 'producer') return row.producer;
  if (viewMode === 'feature_group') return `${row.producer} – ${row.feature_group}`;
  return row.game_title;
}

function MetricsTooltip({ active, payload, viewMode }) {
  if (!active || !payload?.length) return null;
  const g = payload[0].payload;
  return (
    <div className="bg-surface-2 border border-white/10 rounded-md p-2.5 text-[11px] max-w-[220px]">
      <p className="font-bold text-ink mb-1">{rowLabel(g, viewMode)}</p>
      {viewMode === 'game' && <p className="text-ink-dim mb-1.5">{g.provider} · {g.producer}</p>}
      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
        {METRICS.map(m => (
          <React.Fragment key={m.key}>
            <span className="text-ink-dim">{m.label}</span>
            <span className="text-ink text-right">{m.fmt(g[m.key])}</span>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

export default function GameMetricsReport() {
  const navigate = useNavigate();
  const [viewTab, setViewTab] = useState('game');
  const [producerGrouping, setProducerGrouping] = useState('producer');
  // The rows shown: the tab, except By Producer split into feature groups.
  const viewMode = viewTab === 'producer' && producerGrouping === 'feature_group' ? 'feature_group' : viewTab;
  // Rows per view, so switching views doesn't flash another view's data.
  const [rowsByView, setRowsByView] = useState({ game: [], provider: [], producer: [], feature_group: [] });
  const gameRows = rowsByView.game;
  const [featureGroups, setFeatureGroups] = useState([]);
  const [producerCatalog, setProducerCatalog] = useState([]);
  const [producerFilter, setProducerFilter] = useState(ALL);
  const [featureGroup, setFeatureGroup] = useState(ALL);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [range, setRange] = useState(() => rangeForLastDays(7));
  const [provider, setProvider] = useState('all');
  const [metric, setMetric] = useState(METRICS[0].key);
  const [sortCol, setSortCol] = useState(METRICS[0].key);
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);

  const rowNoun = VIEW_SOURCES[viewMode].noun;
  const activeBrand = useBrandChange(() => {
    setProvider('all');
    setProducerFilter(ALL);
    setFeatureGroup(ALL);
    setRowsByView({ game: [], provider: [], producer: [], feature_group: [] });
  });

  // Filter options: every producer (the full catalog from ask-options) and
  // each producer's feature groups. A failed lookup just leaves that list
  // empty; the report itself still loads.
  useEffect(() => {
    let cancelled = false;
    gameDepartmentAPI.getFeatureGroups()
      .then(res => { if (!cancelled) setFeatureGroups(res.data?.feature_groups || []); })
      .catch(() => { if (!cancelled) setFeatureGroups([]); });
    gameDepartmentAPI.getAskOptions()
      .then(res => { if (!cancelled) setProducerCatalog(res.data?.producers || []); })
      .catch(() => { if (!cancelled) setProducerCatalog([]); });
    return () => { cancelled = true; };
  }, [activeBrand]);

  const producers = useMemo(() => producerOptions(producerCatalog, featureGroups), [producerCatalog, featureGroups]);
  const groupsOfProducer = useMemo(() => featureGroupOptions(featureGroups, producerFilter), [featureGroups, producerFilter]);

  function handleProducerFilterChange(next) {
    setProducerFilter(next);
    setFeatureGroup(ALL);
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    const req = { ...toRequestRange(range), ...filterParams(producerFilter, featureGroup) };
    const source = VIEW_SOURCES[viewMode];
    source.fetch(req)
      .then(res => {
        if (cancelled) return;
        setRowsByView(prev => ({ ...prev, [viewMode]: res.data?.[source.field] || [] }));
      })
      .catch(() => { if (!cancelled) setError(`Failed to load ${rowNoun} metrics.`); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, viewMode, activeBrand, producerFilter, featureGroup]);

  const providers = useMemo(() => (
    Array.from(new Set(gameRows.map(g => g.provider).filter(Boolean))).sort()
  ), [gameRows]);

  const filtered = useMemo(() => {
    if (viewMode !== 'game') return rowsByView[viewMode];
    return provider === 'all' ? gameRows : gameRows.filter(g => g.provider === provider);
  }, [viewMode, rowsByView, gameRows, provider]);

  const chartData = useMemo(() => (
    [...filtered]
      .filter(g => g[metric] != null)
      .sort((a, b) => compareNumeric(a[metric], b[metric], 'desc'))
      .slice(0, TOP_N)
      .map(g => ({ ...g, label: truncate(rowLabel(g, viewMode), 24) }))
  ), [filtered, metric, viewMode]);

  const tableRows = useMemo(() => {
    const rows = [...filtered];
    rows.sort((a, b) => {
      const av = a[sortCol];
      const bv = b[sortCol];
      if (!STRING_COLS.has(sortCol)) return compareNumeric(av, bv, sortDir);
      const cmp = String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [filtered, sortCol, sortDir]);

  useEffect(() => { setPage(1); }, [filtered, sortCol, sortDir]);

  const totalPages = Math.max(1, Math.ceil(tableRows.length / TABLE_PAGE_SIZE));
  const pagedRows = useMemo(() => (
    tableRows.slice((page - 1) * TABLE_PAGE_SIZE, page * TABLE_PAGE_SIZE)
  ), [tableRows, page]);

  function resetSort() {
    setSortCol(metric);
    setSortDir('desc');
  }

  function handleViewChange(next) {
    setViewTab(next);
    resetSort();
  }

  function handleGroupingChange(next) {
    setProducerGrouping(next);
    resetSort();
  }

  function handleMetricChange(next) {
    setMetric(next);
    setSortCol(next);
    setSortDir('desc');
  }

  function toggleSort(col) {
    if (sortCol === col) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('desc');
    }
  }

  const activeMetric = METRICS.find(m => m.key === metric);

  function openPerformance(g) {
    navigate('/game-department/performance', {
      state: { game: { unique_game_id: g.unique_game_id, game_title: g.game_title, provider: g.provider } },
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{VIEW_TITLES[viewMode]}</CardTitle>
        <CardDescription>
          Turnover, GGR, active players, bet counts and realized payout per {rowNoun} over the selected date range.
          Provider is the aggregator a game comes through (e.g. relax); producer is the studio behind it (e.g. boomerangstudios). Filter on a producer, and optionally one of its
          feature groups, to see only its games&apos; play.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error && <Alert variant="error" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <ToggleGroup value={viewTab} onChange={handleViewChange}>
            {VIEWS.map(v => <ToggleItem key={v.key} value={v.key}>{v.label}</ToggleItem>)}
          </ToggleGroup>
          {viewTab === 'producer' && (
            <ToggleGroup value={producerGrouping} onChange={handleGroupingChange}>
              {PRODUCER_GROUPINGS.map(g => <ToggleItem key={g.key} value={g.key}>{g.label}</ToggleItem>)}
            </ToggleGroup>
          )}
        </div>

        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div>
            <label className="text-[11px] text-ink-dim mb-1 block">From</label>
            <Input
              type="date" value={range.from} max={range.to}
              onChange={e => e.target.value && setRange(r => ({ ...r, from: e.target.value }))}
              className="w-[150px]"
            />
          </div>
          <div>
            <label className="text-[11px] text-ink-dim mb-1 block">To</label>
            <Input
              type="date" value={range.to} min={range.from} max={todayUtc().toISOString().slice(0, 10)}
              onChange={e => e.target.value && setRange(r => ({ ...r, to: e.target.value }))}
              className="w-[150px]"
            />
          </div>
          <div className="flex items-center gap-1">
            {DATE_RANGE_PRESETS.map(p => (
              <Button key={p.days} variant="ghost" size="sm" onClick={() => setRange(rangeForLastDays(p.days))}>
                {p.label}
              </Button>
            ))}
          </div>
          {viewMode === 'game' && (
            <div>
              <label className="text-[11px] text-ink-dim mb-1 block">Provider</label>
              <Select value={provider} onValueChange={setProvider}>
                <SelectTrigger className="w-[170px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All providers</SelectItem>
                  {providers.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <label className="text-[11px] text-ink-dim mb-1 block">Producer</label>
            <Select value={producerFilter} onValueChange={handleProducerFilterChange}>
              <SelectTrigger className="w-[170px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All producers</SelectItem>
                {producers.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] text-ink-dim mb-1 block">Feature group</label>
            <Select value={featureGroup} onValueChange={setFeatureGroup} disabled={producerFilter === ALL}>
              <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{producerFilter === ALL ? 'Pick a producer first' : 'All feature groups'}</SelectItem>
                {groupsOfProducer.map(g => (
                  <SelectItem key={g.feature_group} value={g.feature_group}>{g.feature_group} ({g.games})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] text-ink-dim mb-1 block">Metric</label>
            <Select value={metric} onValueChange={handleMetricChange}>
              <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {METRICS.map(m => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <span className="text-[11px] text-ink-dim ml-auto">
            {filtered.length} {rowNoun}{filtered.length === 1 ? '' : 's'}
          </span>
        </div>

        {loading ? (
          <div className="flex justify-center py-10"><Spinner size="sm" /></div>
        ) : (
          <>
            <p className="text-[11px] font-bold uppercase tracking-wide text-ink-dim mb-2">
              Top {chartData.length} by {activeMetric?.label.toLowerCase()}
            </p>
            <div className="h-[360px] mb-6">
              {chartData.length === 0 ? (
                <div className="flex items-center justify-center h-full text-xs text-ink-dim">
                  No {rowNoun}s match these filters
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} layout="vertical" margin={{ top: 4, right: 24, bottom: 4, left: 8 }}>
                    <CartesianGrid horizontal={false} stroke="rgba(255,255,255,0.06)" />
                    <XAxis type="number" tickFormatter={activeMetric.fmt} stroke={AXIS_COLOR} fontSize={11} />
                    <YAxis type="category" dataKey="label" width={160} stroke={AXIS_COLOR} fontSize={11} />
                    <Tooltip content={<MetricsTooltip viewMode={viewMode} />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                    <Bar dataKey={metric} fill={METRIC_COLOR} radius={[0, 4, 4, 0]} maxBarSize={18} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="border border-white/10 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    {viewMode === 'game' ? (
                      <>
                        <SortableHead col="game_title" label="Game" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                        <SortableHead col="provider" label="Provider" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                        <SortableHead col="producer" label="Producer" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                      </>
                    ) : viewMode === 'feature_group' ? (
                      <>
                        <SortableHead col="producer" label="Producer" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                        <SortableHead col="feature_group" label="Feature group" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                      </>
                    ) : (
                      <SortableHead col={viewMode} label={viewMode === 'provider' ? 'Provider' : 'Producer'} sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                    )}
                    {METRICS.map(m => (
                      <SortableHead key={m.key} col={m.key} label={m.label} sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} right />
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tableRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={({ game: 3, feature_group: 2 }[viewMode] || 1) + METRICS.length} className="text-center text-ink-dim text-xs py-8">
                        No {rowNoun}s match these filters
                      </TableCell>
                    </TableRow>
                  ) : pagedRows.map(g => (
                    viewMode === 'feature_group' ? (
                      <TableRow key={rowKey(g, viewMode)}>
                        <TableCell className="text-ink-dim">{g.producer}</TableCell>
                        <TableCell className="font-bold text-ink">{g.feature_group}</TableCell>
                        {METRICS.map(m => (
                          <TableCell key={m.key} className="text-right">{m.fmt(g[m.key])}</TableCell>
                        ))}
                      </TableRow>
                    ) : viewMode !== 'game' ? (
                      <TableRow key={rowKey(g, viewMode)}>
                        <TableCell className="font-bold text-ink">{g[viewMode]}</TableCell>
                        {METRICS.map(m => (
                          <TableCell key={m.key} className="text-right">{m.fmt(g[m.key])}</TableCell>
                        ))}
                      </TableRow>
                    ) : (
                      <TableRow
                        key={g.unique_game_id}
                        onClick={() => openPerformance(g)}
                        className="cursor-pointer"
                      >
                        <TableCell className="font-bold text-ink">{g.game_title}</TableCell>
                        <TableCell className="text-ink-dim">{g.provider}</TableCell>
                        <TableCell className="text-ink-dim">{g.producer}</TableCell>
                        {METRICS.map(m => (
                          <TableCell key={m.key} className="text-right">{m.fmt(g[m.key])}</TableCell>
                        ))}
                      </TableRow>
                    )
                  ))}
                </TableBody>
              </Table>
            </div>
            <TablePagination page={page} totalPages={totalPages} total={tableRows.length} onPageChange={setPage} noun={rowNoun} />
          </>
        )}
      </CardContent>
    </Card>
  );
}
